/**
 * Per-tour total floor area shown on the Floorplan Manager card.
 *
 * Storage: gs://{bucket}/tours/{tourId}/area-calculation.json
 *   { value: number, updatedAt: string }
 *
 * Backed by the standalone `tourArea` Cloud Function (gen2, id3d-prod),
 * same anonymous-CORS pattern as tourLayerSettings.
 */

export interface TourArea {
  value: number;
  updatedAt?: string;
}

function stripPrefix(tourId: string): string {
  return tourId.startsWith('tours/') ? tourId.slice(6) : tourId;
}

export async function getTourArea(tourId: string): Promise<TourArea | null> {
  const endpoint = process.env.REACT_APP_AREA_API_URL;
  if (!endpoint) {
    throw new Error('REACT_APP_AREA_API_URL is not configured');
  }
  const url = `${endpoint}?tourId=${encodeURIComponent(
    stripPrefix(tourId),
  )}&_=${Date.now()}`;
  const response = await fetch(url, { method: 'GET' });
  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error(
      `tour area GET returned ${response.status} ${response.statusText}`,
    );
  }
  return (await response.json()) as TourArea;
}

export async function setTourArea(
  tourId: string,
  value: number | null,
): Promise<void> {
  const endpoint = process.env.REACT_APP_AREA_API_URL;
  if (!endpoint) {
    throw new Error('REACT_APP_AREA_API_URL is not configured');
  }
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tourId: stripPrefix(tourId), value }),
  });
  if (!response.ok) {
    let detail = '';
    try {
      const body = await response.json();
      detail = body?.error || '';
    } catch {
      /* ignore */
    }
    throw new Error(
      detail ||
        `tour area POST returned ${response.status} ${response.statusText}`,
    );
  }
}
